/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios';
import auth from '../utilities/authentication';
import { AuthCacher } from '@cimpress-technology/web-shell-communication';
import { isInCometPortal } from '@/utilities/helper';

class HTTPClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL?: string, timeout: number = 10000) {
    this.axiosInstance = axios.create({
      baseURL: baseURL,
      timeout,
    });
  }

  private async getAuthToken(): Promise<string> {
    try {
      if (!isInCometPortal()) {
        return auth.getAccessToken() || '';
      }

      const authCacherPromise = AuthCacher.getAuthCacher();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('AuthCacher timeout')), 3000);
      });
      const authCacher = await Promise.race([
        authCacherPromise,
        timeoutPromise,
      ]);
      return await authCacher.getToken();
    } catch (error) {
      console.error('Error getting token:', error);
      return '';
    }
  }

  private async request<T = any>(
    method: Method,
    suffixUrl: string,
    config: Partial<AxiosRequestConfig> = {},
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.axiosInstance({
      url: suffixUrl,
      method,
      ...config,
      headers: {
        Authorization: `Bearer ${await this.getAuthToken()}`,
        ...(config.headers || {}),
      },
    });

    return response.data;
  }

  async get<T = any>(
    suffixUrl: string,
    params?: unknown,
    customBaseURL?: string,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this.request<T>('get', suffixUrl, {
      params,
      baseURL: customBaseURL ?? this.axiosInstance.defaults.baseURL,
      headers,
    });
  }

  async post<T = any>(
    suffixUrl: string,
    data: unknown,
    params?: unknown,
    timeout?: number,
  ): Promise<T> {
    return this.request<T>('post', suffixUrl, {
      data,
      params,
      timeout: timeout ?? this.axiosInstance.defaults.timeout,
    });
  }

  async put<T = any>(
    suffixUrl: string,
    data: unknown,
    params?: unknown,
    timeout?: number,
  ): Promise<T> {
    return this.request<T>('put', suffixUrl, {
      data,
      params,
      timeout: timeout ?? this.axiosInstance.defaults.timeout,
    });
  }

  async patch<T = any>(
    suffixUrl: string,
    data: unknown,
    params?: unknown,
    timeout?: number,
  ): Promise<T> {
    return this.request<T>('patch', suffixUrl, {
      data,
      params,
      timeout: timeout ?? this.axiosInstance.defaults.timeout,
    });
  }

  async delete<T = any>(suffixUrl: string): Promise<T> {
    return this.request<T>('delete', suffixUrl);
  }
}

export { HTTPClient };
